(() => {
  'use strict';

  const sectionSelector = '[data-contact-form-translations-root]';
  const configSelector = '[data-contact-form-translations]';
  const initializedSections = new WeakSet();

  function parseConfig(section) {
    const configElement = section.querySelector(configSelector);

    if (!configElement) return null;

    try {
      return JSON.parse(configElement.textContent);
    } catch (error) {
      console.error('Unable to parse contact form translations.', error);
      return null;
    }
  }

  function localizedValue(value, locale, fallback) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      value[locale] = fallback;
      return value;
    }

    return fallback;
  }

  function findElements(elements, result = []) {
    if (!Array.isArray(elements)) return result;

    elements.forEach((element) => {
      result.push(element);
      findElements(element.elements, result);
    });

    return result;
  }

  function escapeHtml(value) {
    const element = document.createElement('span');
    element.textContent = value;
    return element.innerHTML;
  }

  function privacyLabelHtml(config) {
    const privacy = config.privacy;
    const link = `<a href="${escapeHtml(privacy.url)}" target="_blank" rel="noopener noreferrer" aria-label="${escapeHtml(privacy.newWindowLabel)}">${escapeHtml(privacy.link)}</a>`;
    return `${escapeHtml(privacy.prefix)}${link}${escapeHtml(privacy.suffix)}`;
  }

  function patchFormConfiguration(config) {
    const formId = String(config.formId);
    const formStore = window.Globo?.FormBuilder?.forms?.[formId] || window.Globo?.FormBuilder?.forms?.[Number(formId)];
    const formConfig = formStore?.[formId];

    if (!formConfig) return false;

    const elements = findElements(formConfig.elements);

    Object.entries(config.fields).forEach(([elementId, translation]) => {
      const element = elements.find((candidate) => candidate.id === elementId);
      if (!element) return;

      element.label = localizedValue(element.label, config.locale, translation.label);
      element.placeholder = localizedValue(element.placeholder, config.locale, translation.placeholder);
    });

    const privacyElement = elements.find((element) => element.id === config.privacy.elementId);
    if (privacyElement) {
      privacyElement.label = localizedValue(privacyElement.label, config.locale, privacyLabelHtml(config));
    }

    if (formConfig.footer) {
      formConfig.footer.submitText = localizedValue(formConfig.footer.submitText, config.locale, config.actions.submit);
      formConfig.footer.previousText = config.actions.previous;
      formConfig.footer.nextText = config.actions.next;
    }

    if (formConfig.errorMessage) {
      Object.entries(config.errors).forEach(([key, translation]) => {
        if (!(key in formConfig.errorMessage)) return;
        formConfig.errorMessage[key] = localizedValue(formConfig.errorMessage[key], config.locale, translation);
      });
    }

    if (formConfig.afterSubmit) {
      formConfig.afterSubmit.title = localizedValue(formConfig.afterSubmit.title, config.locale, config.success.title);
      formConfig.afterSubmit.message = localizedValue(
        formConfig.afterSubmit.message,
        config.locale,
        `<p>${escapeHtml(config.success.message)}</p><p><br></p><p>${escapeHtml(config.success.closing)}</p>`
      );
    }

    return true;
  }

  function updateLabel(control, labelText) {
    const label = control.querySelector('label.globo-label');
    if (!label) return;

    const labelContent = label.querySelector('.label-content');
    if (labelContent) {
      if (labelContent.textContent !== labelText) labelContent.textContent = labelText;
      labelContent.setAttribute('data-label', labelText);
    } else {
      const textNode = Array.from(label.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (textNode && textNode.textContent.trim() !== labelText) textNode.textContent = `${labelText} `;
    }

    label.setAttribute('data-label', labelText);
  }

  function updateField(control, translation) {
    updateLabel(control, translation.label);

    const field = control.querySelector('input:not([type="checkbox"], [type="radio"], [type="hidden"]), textarea, select');
    if (!field) return;

    if (field.getAttribute('placeholder') !== translation.placeholder) {
      field.setAttribute('placeholder', translation.placeholder);
    }
  }

  function updatePrivacy(control, config) {
    const label = control.querySelector('.checkbox-label');
    const labelContent = label?.querySelector('.label-content');
    if (!label || !labelContent) return;

    const currentLink = labelContent.querySelector('a');
    if (
      labelContent.dataset.contactFormTranslation === config.locale &&
      currentLink?.getAttribute('href') === config.privacy.url
    ) {
      return;
    }

    const link = document.createElement('a');
    link.href = config.privacy.url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = config.privacy.link;
    link.setAttribute('aria-label', config.privacy.newWindowLabel);

    labelContent.replaceChildren(
      document.createTextNode(config.privacy.prefix),
      link,
      document.createTextNode(config.privacy.suffix)
    );
    const plainLabel = `${config.privacy.prefix}${config.privacy.link}${config.privacy.suffix}`;
    label.dataset.label = plainLabel;
    labelContent.dataset.label = plainLabel;
    labelContent.dataset.contactFormTranslation = config.locale;
  }

  function replaceDirectText(element, text) {
    const textNode = Array.from(element.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
    );

    if (textNode) {
      if (textNode.textContent.trim() !== text) textNode.textContent = text;
      return;
    }

    element.prepend(document.createTextNode(text));
  }

  function updateSuccess(root, config) {
    const success = root.querySelector('.message.success:not(:empty)');
    if (!success) return;

    const title = success.querySelector('.gfb__content-title, h1, h2, h3, h4, strong');
    const paragraphs = success.querySelectorAll('p');
    const message = paragraphs[0];
    const closing = paragraphs[paragraphs.length - 1];

    if (title && title.textContent !== config.success.title) title.textContent = config.success.title;
    if (message && message.textContent !== config.success.message) message.textContent = config.success.message;
    if (closing && closing !== message && closing.textContent !== config.success.closing) {
      closing.textContent = config.success.closing;
    }

    if (!title && !message) {
      const heading = document.createElement('strong');
      heading.textContent = config.success.title;
      const paragraph = document.createElement('p');
      paragraph.textContent = config.success.message;
      const closingParagraph = document.createElement('p');
      closingParagraph.textContent = config.success.closing;
      success.replaceChildren(heading, paragraph, closingParagraph);
    }

    success.dataset.contactFormTranslation = config.locale;
  }

  function translateForm(root, config) {
    Object.entries(config.fields).forEach(([elementId, translation]) => {
      const control = root.querySelector(`.globo-form-control[data-element-id="${CSS.escape(elementId)}"]`);
      if (control) updateField(control, translation);
    });

    const privacyControl = root.querySelector(
      `.globo-form-control[data-element-id="${CSS.escape(config.privacy.elementId)}"]`
    );
    if (privacyControl) updatePrivacy(privacyControl, config);

    const submit = root.querySelector('.gfb__footer button.submit');
    if (submit) {
      replaceDirectText(submit, config.actions.submit);
      const submitTemplate = submit.getAttribute('data-submit-text');

      if (submitTemplate) {
        const template = document.createElement('template');
        template.innerHTML = submitTemplate;
        replaceDirectText(template.content, config.actions.submit);
        submit.setAttribute('data-submit-text', template.innerHTML);
      }
    }

    updateSuccess(root, config);
  }

  function initializeSection(section) {
    if (initializedSections.has(section)) return;

    const config = parseConfig(section);
    if (!config) return;

    initializedSections.add(section);

    let scheduled = false;
    let configurationPatched = false;
    let configurationAttempts = 0;
    let configurationRetryTimer = null;

    const ensureConfiguration = () => {
      if (configurationPatched || configurationRetryTimer) return;

      configurationAttempts += 1;
      configurationPatched = patchFormConfiguration(config);

      if (!configurationPatched && configurationAttempts < 20) {
        configurationRetryTimer = window.setTimeout(() => {
          configurationRetryTimer = null;
          ensureConfiguration();
        }, 100);
      }
    };

    const applyTranslations = () => {
      if (scheduled) return;
      scheduled = true;

      queueMicrotask(() => {
        scheduled = false;
        ensureConfiguration();

        const root = section.querySelector(`#globo-formbuilder-${CSS.escape(String(config.formId))}`);
        if (root) translateForm(root, config);
      });
    };

    const observer = new MutationObserver(applyTranslations);
    observer.observe(section, { childList: true, subtree: true });

    applyTranslations();
  }

  function initialize(container = document) {
    if (container.matches?.(sectionSelector)) initializeSection(container);
    container.querySelectorAll?.(sectionSelector).forEach(initializeSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
